// Shared helpers for the Data Tools page: formatting, plain-English descriptions, presets and CSV export.

const ENUM_LABELS = {
    ASSET_RECOVERED: "Asset Recovered",
    DISCOUNTED_PAYMENT: "Discounted Payment",
    PARTIAL_PAID: "Partially Paid",
    MISCELLANEOUS: "Miscellaneous",
};

export const prettyEnum = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    if (ENUM_LABELS[value]) return ENUM_LABELS[value];
    return String(value)
        .toLowerCase()
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const formatDate = (iso) => {
    if (!iso) return "—";
    const [y, m, d] = String(iso).split("-");
    if (!y || !m || !d) return String(iso);
    return `${Number(d)} ${MONTHS[Number(m) - 1]} ${y}`;
};

const formatPeriod = (value, bucket) => {
    if (value === null || value === undefined) return "(no date)";
    const [y, m] = String(value).split("-");
    if (bucket === "YEAR") return y;
    if (bucket === "MONTH") return `${MONTHS[Number(m) - 1]} ${y}`;
    return formatDate(value);
};

export const formatMoney = (value) => {
    const n = Number(value ?? 0);
    return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
};

export const formatValue = (value, type, bucket) => {
    if (type === "PERIOD") return formatPeriod(value, bucket);
    if (value === null || value === undefined || value === "") return "—";
    switch (type) {
        case "MONEY":
            return formatMoney(value);
        case "NUMBER":
            return Number(value).toLocaleString("en-IN", { maximumFractionDigits: 2 });
        case "DATE":
            return formatDate(value);
        case "ENUM":
            return prettyEnum(value);
        case "BOOLEAN":
            return value === true || Number(value) === 1 ? "Yes" : "No";
        default:
            return String(value);
    }
};

export const badgeClass = (value) => {
    const v = String(value || "");
    if (["PAID", "CLOSED"].includes(v)) return "bg-green-100 text-green-700";
    if (["OVERDUE", "DEFAULTED", "CANCELLED"].includes(v)) return "bg-red-100 text-red-700";
    if (v.startsWith("PARTIAL")) return "bg-yellow-100 text-yellow-800";
    if (["ACTIVE"].includes(v)) return "bg-blue-100 text-blue-700";
    if (["SETTLED", "ASSET_RECOVERED", "DISCOUNTED_PAYMENT"].includes(v)) return "bg-purple-100 text-purple-700";
    if (["PAUSED"].includes(v)) return "bg-orange-100 text-orange-700";
    return "bg-gray-100 text-gray-700";
};

export const isNumericType = (type) => type === "MONEY" || type === "NUMBER";

// ---------------------------------------------------------------- sorting words

export const sortChoices = (type) => {
    if (type === "DATE") return [{ value: "DESC", label: "Newest first" }, { value: "ASC", label: "Oldest first" }];
    if (isNumericType(type)) return [{ value: "DESC", label: "Highest first" }, { value: "ASC", label: "Lowest first" }];
    return [{ value: "ASC", label: "A → Z" }, { value: "DESC", label: "Z → A" }];
};

export const METRIC_FUNCTIONS = [
    { value: "COUNT", label: "Count how many", needsField: false },
    { value: "SUM", label: "Add up (total)", needsField: true, types: ["MONEY", "NUMBER"] },
    { value: "AVG", label: "Average", needsField: true, types: ["MONEY", "NUMBER"] },
    { value: "MIN", label: "Lowest / earliest", needsField: true, types: ["MONEY", "NUMBER", "DATE"] },
    { value: "MAX", label: "Highest / latest", needsField: true, types: ["MONEY", "NUMBER", "DATE"] },
];

export const DATE_BUCKETS = [
    { value: "DAY", label: "per day" },
    { value: "MONTH", label: "per month" },
    { value: "YEAR", label: "per year" },
];

// ---------------------------------------------------------------- query helpers

export const emptyQuery = (dataset) => ({
    mode: "LIST",
    columns: dataset.fields.filter((f) => f.defaultVisible).map((f) => f.key),
    filters: [],
    matchType: "ALL",
    sort: { field: dataset.defaultSortField, direction: dataset.defaultSortDirection },
    limit: 100,
    summary: { groupBy: "", dateBucket: "MONTH", metrics: [{ function: "COUNT", field: null }] },
});

export const newFilter = (field, operators) => {
    const ops = operators[field.type] || [];
    return { field: field.key, operator: ops[0]?.key, value: ops[0]?.input === "DAYS" ? "30" : "", valueTo: "", values: [] };
};

/** Turns the builder state into the request body the API expects. workshopId is mandatory. */
export const toRequest = (workshopId, datasetKey, query) => ({
    workshopId: Number(workshopId),
    dataset: datasetKey,
    mode: query.mode,
    columns: query.columns,
    filters: query.filters,
    matchType: query.matchType,
    sort: query.mode === "LIST" ? query.sort : query.summarySort || null,
    limit: query.limit,
    summary: query.mode === "SUMMARY"
        ? {
            groupBy: query.summary.groupBy || null,
            dateBucket: query.summary.dateBucket,
            metrics: query.summary.metrics.map((m) => ({ function: m.function, field: m.function === "COUNT" ? null : m.field })),
        }
        : null,
});

/** Checks the builder for missing values so we can show a friendly hint before calling the server. */
export const validateQuery = (dataset, query, operators) => {
    for (const f of query.filters) {
        const field = dataset.fields.find((x) => x.key === f.field);
        const op = (operators[field?.type] || []).find((o) => o.key === f.operator);
        if (!field || !op) return "One of your conditions is incomplete.";
        if (op.input === "SINGLE" && !String(f.value || "").trim()) return `Please fill in a value for “${field.label}”.`;
        if (op.input === "RANGE" && (!String(f.value || "").trim() || !String(f.valueTo || "").trim()))
            return `Please fill in both values for “${field.label}”.`;
        if (op.input === "MULTI" && (!f.values || f.values.length === 0)) return `Please pick at least one option for “${field.label}”.`;
        if (op.input === "DAYS" && !(Number(f.value) >= 1)) return `Please enter a number of days for “${field.label}”.`;
    }
    if (query.mode === "LIST" && query.columns.length === 0) return "Please pick at least one column to show.";
    if (query.mode === "SUMMARY") {
        if (query.summary.metrics.length === 0) return "Please add at least one calculation.";
        for (const m of query.summary.metrics) {
            if (m.function !== "COUNT" && !m.field) return "Please choose what to calculate for each calculation.";
        }
    }
    return null;
};

/** A plain-English sentence describing what the query does. */
export const describeQuery = (dataset, query, operators) => {
    if (!dataset) return "";
    const fieldLabel = (key) => dataset.fields.find((f) => f.key === key)?.label || key;

    const conditions = query.filters.map((f) => {
        const field = dataset.fields.find((x) => x.key === f.field);
        const op = (operators[field?.type] || []).find((o) => o.key === f.operator);
        if (!field || !op) return null;
        const show = (v) => (field.type === "DATE" ? formatValue(v, "DATE") : field.type === "MONEY" ? formatMoney(v) : v);
        switch (op.input) {
            case "NONE":
                return `${field.label} ${op.label}`;
            case "DAYS":
                return `${field.label} is in the ${op.key === "LAST_N_DAYS" ? "last" : "next"} ${f.value} days`;
            case "RANGE":
                return `${field.label} is between ${show(f.value)} and ${show(f.valueTo)}`;
            case "MULTI":
                return `${field.label} ${op.label} ${(f.values || []).map(prettyEnum).join(", ")}`;
            default:
                return `${field.label} ${op.label} “${show(f.value)}”`;
        }
    }).filter(Boolean);

    let sentence;
    if (query.mode === "SUMMARY") {
        const calcs = query.summary.metrics.map((m) => {
            const fn = METRIC_FUNCTIONS.find((x) => x.value === m.function);
            return m.function === "COUNT" ? `count of ${dataset.recordNoun}` : `${fn?.label.toLowerCase()} of ${fieldLabel(m.field)}`;
        });
        sentence = `Summary of ${dataset.label}: ${calcs.join(", ")}`;
        if (query.summary.groupBy) {
            const g = dataset.fields.find((f) => f.key === query.summary.groupBy);
            const bucket = g?.type === "DATE" ? ` ${DATE_BUCKETS.find((b) => b.value === query.summary.dateBucket)?.label}` : "";
            sentence += `, grouped by ${g?.label}${bucket}`;
        }
    } else {
        sentence = `Showing ${dataset.label}`;
    }
    if (conditions.length) {
        sentence += ` where ${conditions.join(query.matchType === "ANY" ? " or " : " and ")}`;
    }
    if (query.mode === "LIST" && query.sort?.field) {
        const sortField = dataset.fields.find((f) => f.key === query.sort.field);
        const dir = sortChoices(sortField?.type).find((c) => c.value === query.sort.direction)?.label;
        sentence += `, sorted by ${sortField?.label} (${dir?.toLowerCase()})`;
    }
    return sentence + ".";
};

// ---------------------------------------------------------------- quick questions

const cond = (field, operator, extra = {}) => ({ field, operator, value: "", valueTo: "", values: [], ...extra });
const summary = (groupBy, metrics, dateBucket = "MONTH") => ({ groupBy, dateBucket, metrics });

/** Ready-made questions. `query` is merged onto the dataset's empty query. */
export const PRESETS = [
    {
        id: "overdue-emis",
        label: "Overdue EMIs",
        hint: "EMIs past their due date that aren't fully paid",
        dataset: "emis",
        query: {
            columns: ["loanId", "customerName", "customerPhone", "dueDate", "status", "amount", "penalty", "remainingAmount", "daysLate"],
            filters: [cond("status", "ANY_OF", { values: ["OVERDUE", "PARTIALLY_PAID", "PENDING"] }), cond("dueDate", "IN_PAST")],
            sort: { field: "dueDate", direction: "ASC" },
        },
    },
    {
        id: "emis-due-soon",
        label: "EMIs due in the next 7 days",
        hint: "Who to remind this week",
        dataset: "emis",
        query: {
            columns: ["loanId", "customerName", "customerPhone", "dueDate", "status", "amount", "remainingAmount"],
            filters: [cond("status", "ANY_OF", { values: ["PENDING", "PARTIALLY_PAID"] }), cond("dueDate", "NEXT_N_DAYS", { value: "7" })],
            sort: { field: "dueDate", direction: "ASC" },
        },
    },
    {
        id: "collections-this-month",
        label: "Money collected this month",
        hint: "Every EMI payment received this month",
        dataset: "emiPayments",
        query: { filters: [cond("paymentDate", "THIS_MONTH")] },
    },
    {
        id: "collections-per-month",
        label: "Collections per month",
        hint: "Total EMI money received each month (last 12 months)",
        dataset: "emiPayments",
        query: {
            mode: "SUMMARY",
            filters: [cond("paymentDate", "LAST_N_DAYS", { value: "365" })],
            summary: summary("paymentDate", [{ function: "SUM", field: "amountPaid" }, { function: "COUNT", field: null }]),
        },
    },
    {
        id: "loans-per-month",
        label: "Loans given per month",
        hint: "How many loans and how much was financed each month",
        dataset: "loans",
        query: {
            mode: "SUMMARY",
            filters: [cond("startDate", "LAST_N_DAYS", { value: "365" })],
            summary: summary("startDate", [{ function: "COUNT", field: null }, { function: "SUM", field: "financedAmount" }]),
        },
    },
    {
        id: "active-loans-by-branch",
        label: "Active loans by branch",
        hint: "Money still due, branch by branch",
        dataset: "loans",
        query: {
            mode: "SUMMARY",
            filters: [cond("status", "ANY_OF", { values: ["ACTIVE"] })],
            summary: summary("branch", [
                { function: "COUNT", field: null },
                { function: "SUM", field: "financedAmount" },
                { function: "SUM", field: "principalDue" },
            ]),
        },
    },
    {
        id: "expenses-by-category",
        label: "Expenses by category (this year)",
        hint: "Where the money is going",
        dataset: "expenses",
        query: {
            mode: "SUMMARY",
            filters: [cond("expenseDate", "THIS_YEAR")],
            summary: summary("expenseType", [{ function: "SUM", field: "amount" }, { function: "COUNT", field: null }]),
        },
    },
    {
        id: "unpaid-bills",
        label: "Unpaid workshop bills",
        hint: "Bills with money still to collect",
        dataset: "bills",
        query: {
            filters: [cond("status", "ANY_OF", { values: ["PENDING", "PARTIAL_PAID"] })],
            sort: { field: "issueDate", direction: "ASC" },
        },
    },
];

export const presetQuery = (dataset, preset) => {
    const base = emptyQuery(dataset);
    return {
        ...base,
        ...preset.query,
        summary: { ...base.summary, ...(preset.query.summary || {}) },
        sort: preset.query.sort || base.sort,
        columns: preset.query.columns || base.columns,
    };
};

// ---------------------------------------------------------------- export

export const downloadCsv = (fileName, columns, rows) => {
    const escape = (v) => {
        const s = v === null || v === undefined ? "" : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const exportValue = (value, col) => {
        if (col.type === "PERIOD") return formatValue(value, col.type, col.bucket);
        if (col.type === "ENUM" || col.type === "BOOLEAN") return formatValue(value, col.type);
        return value; // keep numbers and ISO dates raw so Excel can do maths and sort them
    };
    const lines = [
        columns.map((c) => escape(c.label)).join(","),
        ...rows.map((r) => columns.map((c) => escape(exportValue(r[c.key], c))).join(",")),
    ];
    // BOM so Excel opens ₹ and other characters correctly.
    const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

// ---------------------------------------------------------------- saved views (per browser)

const savedViewsKey = (workshopId) => `dataTools.savedViews.${workshopId}`;

export const loadSavedViews = (workshopId) => {
    try {
        return JSON.parse(localStorage.getItem(savedViewsKey(workshopId))) || [];
    } catch (e) {
        return [];
    }
};

export const storeSavedViews = (workshopId, views) => {
    try {
        localStorage.setItem(savedViewsKey(workshopId), JSON.stringify(views));
    } catch (e) {
        // storage unavailable (private mode / quota) — saved views just won't persist
    }
};
